import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Check, ArrowRight, Eye, Sparkles, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IMAGES } from "@/lib/constants";
import { BookingFlow } from "@/components/BookingFlow";
import { useGuestyListings } from "@/components/LiveAvailability";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const domeAmenities = [
  "Skylight View", "Queen Bed", "Climate Control", "Private Entry",
  "Outdoor Deck", "Ambient Lighting", "USB Charging", "Parking",
];

export default function DomesCabinsPage() {
  const { listings } = useGuestyListings("dome");
  const maxGuests = listings.length ? Math.max(...listings.map((l) => l.accommodates || 0)) : 0;
  return (
    <>
      <Helmet>
        <title>Domes & Cabins | Unique Stays Near Lake Texoma - Texoma Resorts</title>
        <meta name="description" content="Stay in our geodesic domes with panoramic sky views or cozy cabins near Lake Texoma. Unique accommodations at Serenity Village, Denison Texas." />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.dome1} alt="Geodesic dome at Texoma Resorts" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Unique Experiences</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Domes & Cabins
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
              <div className="img-hover-zoom rounded-lg overflow-hidden">
                <img src={IMAGES.dome2} alt="Geodesic dome in nature" className="w-full aspect-[4/3] object-cover" />
              </div>
            </motion.div>
            <motion.div {...fadeUp}>
              <p className="caption-text text-accent mb-3">Geodesic Domes</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-6">
                Sleep Under the Stars
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                Our geodesic domes offer a one-of-a-kind experience with panoramic views of
                the Texas sky. Modern comforts meet architectural wonder in these stunning structures.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-6">
                Perfect for romantic getaways and couples looking for something truly unique.
                Wake up to sunrise views and fall asleep counting stars.
              </p>
              <Badge data-testid="domes-guests-badge" className="bg-primary text-white font-body text-xs mb-6">Up to {maxGuests || 2} Guests</Badge>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {domeAmenities.map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{a}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 mb-8">
                {[
                  { icon: Eye, text: "360 Views" },
                  { icon: Sparkles, text: "Romantic" },
                  { icon: Camera, text: "Instagram-Worthy" },
                ].map((h) => (
                  <Badge key={h.text} variant="outline" className="font-body text-xs flex items-center gap-1">
                    <h.icon size={12} /> {h.text}
                  </Badge>
                ))}
              </div>
              <a href="#book">
                <Button data-testid="dome-book-now" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
                  Check Availability <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="book" data-testid="domes-booking-section" className="py-16 md:py-24 bg-white border-y">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="caption-text text-accent mb-3">Book Direct & Save</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-2">
              Book a Dome
            </h2>
            <p className="font-body text-sm text-muted-foreground mb-6">
              Choose your dome, select dates on the calendar, and pay securely — your
              reservation is confirmed instantly.
            </p>
            <BookingFlow listingFilter="dome" />
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#1A3C34]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="caption-text text-white/50 mb-3">More Cabins</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-white tracking-tight mb-6">
              Cozy Cabin Retreats
            </h2>
            <p className="font-body text-base text-white/60 max-w-2xl mx-auto mb-8 leading-relaxed">
              In addition to our signature Bet Cabin, we offer cozy cabin options
              perfect for smaller groups and couples. Each cabin is fully equipped
              with modern amenities while maintaining that rustic lakeside charm.
            </p>
            <Link to="/bet-cabin">
              <Button data-testid="cabins-book-now" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
                View Available Cabins <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
